'use client';

import React from 'react';

import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import { LuMail } from 'react-icons/lu';

export function Newsletter() {
  const formId = React.useId();
  const { t } = useTranslations();
  const emailLabel = t('text183');

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (request: Parameters<typeof newsletterSubscribe>[0]) => {
      const api = await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return api.newsletterSubscribe(request);
    },
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('relative z-10 w-full', 'ea-homepage-newsletter')}>
      <form
        className={cn('grid w-full grid-cols-12 gap-6', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'col-span-12 mx-auto flex w-full max-w-[628px] flex-col justify-center gap-2 sm:gap-4 md:col-span-12 lg:gap-8',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex h-12 w-full flex-row items-center sm:h-16')}>
            <Input
              aria-label={emailLabel && emailLabel !== 'text183' ? emailLabel : 'E-mail'}
              id={`${formId}-email`}
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={e => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'h-12 border-none bg-white text-muted-foreground sm:h-16',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
                main: 'pl-6',
              }}
              after={
                <Button
                  type="submit"
                  className={cn(
                    'mr-0.5 hidden h-[52px] sm:flex',
                    'ea-homepage-newsletter__form-item-email-submit-button',
                  )}
                  disabled={email.length === 0 || !checked || isSubscribed}
                  isLoading={subscribeMutation.isPending}
                  aria-label={t('text189')}
                >
                  {t('text189')}
                </Button>
              }
            />
          </div>
          <Button
            type="submit"
            className={cn(
              'flex h-12 sm:hidden',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
            aria-label={t('text189')}
          >
            {t('text189')}
          </Button>
          {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'mt-6 flex w-full flex-row items-start justify-center space-x-2 sm:items-center lg:mt-0',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id={`${formId}-gdpr`}
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('border-none', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`${formId}-gdpr`}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span
                className={cn(
                  'text-sm font-light text-white',
                  'ea-homepage-newsletter__form-item-gdpr-checkbox-span',
                )}
              >
                {t('text264')}&nbsp;
                <a
                  href={t('text391') ?? '#'}
                  className={cn(
                    'text-sm font-light text-white',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392') ?? '#'}
                  className={cn(
                    'text-sm font-light text-white',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
